import {
  Badge,
  Box,
  Button,
  Center,
  CircularProgress,
  Divider,
  Flex,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  PopUpMessageContext,
  ScreenContext,
  useAuth,
  useCollection,
  useIsLeadAdmin,
  usePracticeAccess,
} from '@hb/react'
import {
  Expandable,
  LoadingOverlay,
  practiceSelectField,
  StandaloneInput,
  useDocument,
  USERS_REF,
} from '@hb/react-web'
import { colors, MIDWIVES, midwivesCollection, Practice } from '@hb/shared'
import { doc, updateDoc } from 'firebase/firestore'
import { FC, useCallback, useContext, useMemo, useState } from 'react'

const SelectedIndicatorSvg: FC<{ isSelected: boolean }> = ({ isSelected }) => (
  <svg
    width="18px"
    height="18px"
    viewBox="-7 -7 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <circle r="6" strokeWidth="1" stroke="#cdcdcd" />
    <circle r="5.5" fill="#efefef" />
    {isSelected ? <circle r="4" fill={colors.green.hex} /> : null}
  </svg>
)

const PracticeAccessView: FC<{
  practiceId: string
  onOpen: () => void
  isOpen: boolean
  isSelected: boolean
  onSelect: () => Promise<void>
  isLeadAdmin: boolean
}> = ({ practiceId, isSelected, onSelect, isLeadAdmin }) => {
  const { loading, data } = useDocument<Practice>(MIDWIVES, practiceId)
  const { practiceAccess } = usePracticeAccess()
  // const delayedLoading = useLoadingTimeout(loading, 500)
  const [selectLoading, setSelectLoading] = useState(false)

  const handleSelect = useCallback(() => {
    setSelectLoading(true)
    onSelect().finally(() => setSelectLoading(false))
  }, [onSelect])

  const { isMobile } = useContext(ScreenContext)
  return (
    <Button
      variant="unstyled"
      px={3}
      bg="white"
      _hover={{ bg: 'gray.50' }}
      display="flex"
      cursor="pointer"
      minH="0"
      onClick={handleSelect}
      transition="all 300ms"
      alignItems={isMobile ? 'flex-start' : 'center'}
      aria-label={data ? data.name || 'Unnamed Provider' : 'Loading...'}
      flexFlow={isMobile ? 'column' : 'row'}
      w="100%">
      {loading ? (
        <>
          <CircularProgress size={6} color={colors.green.hex} isIndeterminate />
          <Text color="gray.600">Loading practice info...</Text>
        </>
      ) : (
        <>
          {/* <HStack flex={1}> */}
          {!isLeadAdmin ? (
            <Flex gap={2} alignItems="center">
              <Badge bg={colors.green.hex} fontSize="xs" color="white">
                {practiceAccess?.[practiceId] || 'User'}
              </Badge>
              <Text>{data?.name}</Text>
            </Flex>
          ) : null}
          {/* </HStack> */}
          <Center cursor="pointer" ml="auto">
            {selectLoading ? (
              <CircularProgress size={5} color={colors.green.hex} isIndeterminate />
            ) : (
              <HStack spacing={1}>
                {isSelected ? (
                  <Text fontSize="xs" color={colors.green.hex}>
                    ACTIVE
                  </Text>
                ) : null}
                <SelectedIndicatorSvg isSelected={isSelected} />
              </HStack>
            )}
          </Center>
        </>
      )}
    </Button>
  )
}

const PracticeMemberPracticeSelect = ({
  practiceIds,
  isLeadAdmin,
  selectedPracticeId,
  onSelect,
  viewedPracticeId,
  onOpen,
}: {
  practiceIds: string[]
  isLeadAdmin: boolean
  selectedPracticeId: string | null
  onSelect: (id: string) => Promise<void>
  viewedPracticeId: string | null
  onOpen: (id: string) => void
}) => (
  <VStack divider={<Divider />} fontFamily="Comfortaa" spacing={0} w="100%" align="flex-start">
    {practiceIds.map(practiceId => (
      <PracticeAccessView
        isLeadAdmin={isLeadAdmin}
        isSelected={selectedPracticeId === practiceId}
        onSelect={() => onSelect(practiceId)}
        onOpen={() => onOpen(practiceId)}
        isOpen={viewedPracticeId === practiceId}
        key={practiceId}
        practiceId={practiceId}
      />
    ))}
  </VStack>
)

const AdminPracticeSelect = ({
  onSelect,
  selectedPracticeId,
}: {
  onSelect: (id: string) => Promise<void>
  selectedPracticeId: string | null
}) => {
  const [isLoading, setIsLoading] = useState(false)

  const onChange = useCallback(
    async (value: string) => {
      setIsLoading(true)
      await onSelect(value)
      setIsLoading(false)
    },
    [onSelect],
  )

  return (
    <Flex py={2} px={4} flexFlow="column" w="100%">
      <Text fontWeight={500} color="gray.600">
        Selected Practice
      </Text>
      <Box position="relative">
        <StandaloneInput
          theme="detailed"
          value={selectedPracticeId}
          field={practiceSelectField}
          onChange={onChange}
        />
        <LoadingOverlay loading={isLoading} />
      </Box>
    </Flex>
  )
}

const useAvailablePracticeIds = () => {
  const { practiceAccess, selectedPracticeId } = usePracticeAccess()
  const isLeadAdmin = useIsLeadAdmin()

  const { items: allPractices } = useCollection(midwivesCollection)

  const practiceIds = useMemo(() => {
    // presort by name
    const presorted = (
      isLeadAdmin ? allPractices.map(p => p.id) : Object.keys(practiceAccess ?? {})
    ).sort((a, b) => {
      const aName = allPractices.find(p => p.id === a)?.name ?? ''
      const bName = allPractices.find(p => p.id === b)?.name ?? ''
      return aName.localeCompare(bName)
    })
    // sort by selected first
    return selectedPracticeId
      ? [selectedPracticeId, ...presorted.filter(id => id !== selectedPracticeId)]
      : presorted
  }, [practiceAccess, isLeadAdmin, allPractices, selectedPracticeId])

  return { practiceIds, isLeadAdmin, allPractices }
}

export const UserPracticeAccess = () => {
  const { selectedPracticeId } = usePracticeAccess()

  const { showError } = useContext(PopUpMessageContext)
  const authUser = useAuth(s => s.authUser)
  const ref = useMemo(() => (authUser ? doc(USERS_REF, authUser.uid) : null), [authUser])
  const selectPractice = useCallback(
    async (id: string) => {
      if (!ref) {
        console.error('no ref')
        return
      }
      try {
        await updateDoc(ref, { selectedPracticeId: id })
      } catch (e: any) {
        showError(e?.message || 'Error selecting practice')
      }
    },
    [ref, showError],
  )

  const { practiceIds, isLeadAdmin } = useAvailablePracticeIds()

  const [viewedPracticeId, setViewedPracticeId] = useState<string | null>(null)

  if (!practiceIds.length) {
    return (
      <Flex p={2} w="100%">
        <Text>You have not been added to a practice</Text>
      </Flex>
    )
  }

  return isLeadAdmin ? (
    <AdminPracticeSelect onSelect={selectPractice} selectedPracticeId={selectedPracticeId} />
  ) : (
    <Expandable
      initExpanded
      headerProps={{ bg: 'gray.50', overflow: 'hidden' }}
      borderRadius={4}
      border="1px solid #cdcdcd"
      header={() => (
        <Text borderRadius={4} bg="gray.50" py={1} px={2} fontFamily="Comfortaa">
          My Practices
        </Text>
      )}>
      <PracticeMemberPracticeSelect
        practiceIds={practiceIds}
        isLeadAdmin={isLeadAdmin}
        selectedPracticeId={selectedPracticeId}
        onSelect={selectPractice}
        viewedPracticeId={viewedPracticeId}
        onOpen={setViewedPracticeId}
      />
    </Expandable>
  )
}
